import { cssSpacing, spacing, typography as designTypography } from "../../../app/design-system";
import { Divider,theme,Typography } from "../../../../../../../shared/frontend/ui";
import { WALLET_REASON_LABEL_KEYS,WALLET_TYPE_POINTS } from "../../../app/constants/walletType";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateTimeIso,formatMoney,formatNumber,formatWalletBalance } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";

const WALLET_HISTORY_TYPE_UPDATED = "updated";

const WALLET_HISTORY_BALANCE_FONT_SIZE = "0.9em";

export type WalletHistoryRow = {
  timestamp: string;
  type: string;
  amount: number | null;
  balanceBefore: number;
  balanceAfter: number;
  description: string;
  reason?: number | null;
  paymentMethod?: number | null;
};

type Props = {
  history: WalletHistoryRow[];
  loading: boolean;
  walletType: number | null | undefined;
  currencyCode: string;
  text: I18nContextValue["text"];
  units: UnitsSettings;
};

function buildWalletHistoryDescription(
  row: WalletHistoryRow,
  walletType: number | null | undefined,
  currencyCode: string,
  text: I18nContextValue["text"],
  units: UnitsSettings
) {
  let description = row.description;
  const normalizedWalletType = Number(walletType || 0);
  const isPoints = normalizedWalletType === WALLET_TYPE_POINTS;
  const unitLabel = isPoints ? text("point") : currencyCode;
  const unitLabelPlural = isPoints ? text("points") : currencyCode;

  if (row.amount != null && row.amount !== 0) {
    const absAmount = Math.abs(row.amount);
    const formattedAmount = isPoints
      ? formatNumber(absAmount, units)
      : formatMoney(absAmount, units);

    // Punkte: auf-/abgebucht; Geld: ein-/ausgezahlt.
    const pastVerb = isPoints
      ? (row.amount > 0 ? text("credited (past)") : text("debited (past)"))
      : (row.amount > 0 ? text("deposited (past)") : text("withdrawn (past)"));
    description = `${formattedAmount} ${absAmount === 1 ? unitLabel : unitLabelPlural} ${pastVerb}`;
  }

  return description;
}

/** Herkunfts-Label (Grund) für einen Verlaufseintrag, übersetzt. Leer, wenn kein Grund hinterlegt. */
function buildWalletHistoryReason(
  row: WalletHistoryRow,
  text: I18nContextValue["text"]
): string {
  if (row.reason == null) return "";
  const key = WALLET_REASON_LABEL_KEYS[Number(row.reason)];
  return key ? text(key) : "";
}

function formatHistoryDate(timestamp: string, units: UnitsSettings): string {
  return formatDateTimeIso(timestamp, units, { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatHistoryTime(timestamp: string, units: UnitsSettings): string {
  return formatDateTimeIso(timestamp, units, { hour: "2-digit", minute: "2-digit" });
}

export function PersonWalletHistorySection({ history, loading, walletType, currencyCode, text, units }: Props) {
  const { token } = theme.useToken();
  const normalizedWalletType = Number(walletType || 0);
  const visibleHistory = history.filter((row) => {
    if (row.type === WALLET_HISTORY_TYPE_UPDATED && row.balanceBefore === row.balanceAfter && (row.amount == null || row.amount === 0)) {
      return false;
    }
    return true;
  });
  const isPointsWallet = normalizedWalletType === WALLET_TYPE_POINTS;
  const balanceLabel = text("Balance (history)");
  const historyTitle = isPointsWallet ? text("Usage history") : text("Transaction history");

  return (
    <>
      <Divider style={{ margin: cssSpacing(spacing.md, 0) }} />
      <Typography.Text type="secondary">{historyTitle}</Typography.Text>
      {loading ? (
        <div style={{ marginTop: spacing.sm, padding: spacing.md, background: token.colorFillAlter, borderRadius: token.borderRadius }}>
          <Typography.Text type="secondary">{text("Loading history...")}</Typography.Text>
        </div>
      ) : history.length === 0 ? (
        <div style={{ marginTop: spacing.sm, padding: spacing.md, background: token.colorFillAlter, borderRadius: token.borderRadius }}>
          <Typography.Text type="secondary">{text("No history available.")}</Typography.Text>
        </div>
      ) : (
        <div style={{ marginTop: spacing.sm }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: spacing.sm }}>
            {visibleHistory.map((row, index) => {
              const description = buildWalletHistoryDescription(row, normalizedWalletType, currencyCode, text, units);
              const reasonLabel = buildWalletHistoryReason(row, text);
              const showPaymentMethod = !isPointsWallet && row.paymentMethod != null;
              const meta = [reasonLabel, showPaymentMethod ? `${text("Payment method")}: ${paymentMethodLabel(row.paymentMethod ?? null)}` : ""]
                .filter(Boolean)
                .join(" · ");

              return (
                <div
                  key={index}
                  style={{
                    padding: spacing.md,
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadius,
                    display: "grid",
                    gridTemplateColumns: "96px 1fr auto",
                    gap: spacing.md,
                    alignItems: "center"
                  }}
                >
                  <div>
                    <Typography.Text strong style={{ display: "block", lineHeight: designTypography.lineHeight.normal }}>
                      {formatHistoryTime(row.timestamp, units)}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ display: "block", fontSize: designTypography.fontSize.sm, lineHeight: designTypography.lineHeight.tight }}>
                      {formatHistoryDate(row.timestamp, units)}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ display: "block" }}>{description}</Typography.Text>
                    {meta ? (
                      <Typography.Text type="secondary" style={{ display: "block", fontSize: designTypography.fontSize.sm, lineHeight: designTypography.lineHeight.tight }}>
                        {meta}
                      </Typography.Text>
                    ) : null}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {row.amount != null && row.amount !== 0 && (
                      <Typography.Text type={row.amount > 0 ? "success" : "danger"} style={{ fontWeight: "bold" }}>
                        {row.amount > 0 ? "+" : ""}{formatWalletBalance(row.amount, normalizedWalletType, units)}
                      </Typography.Text>
                    )}
                    <Typography.Text type="secondary" style={{ display: "block", fontSize: WALLET_HISTORY_BALANCE_FONT_SIZE }}>
                      {balanceLabel}: {formatWalletBalance(row.balanceAfter, normalizedWalletType, units)}
                    </Typography.Text>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
